import { Check, Copy, Download, Sparkles, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import type { BannersConfig, CreateShortlinkResponse, Product } from '@/types'
import { useBrand } from '@/brand/BrandProvider'
import { api } from '@/lib/api'
import { currency } from '@/lib/format'
import { buildVisibleShortUrl } from '@/lib/shortlink'

const BANNER_FORMATS = [
  { key: 'story', label: 'Story', size: '1080×1920' },
  { key: 'post', label: 'Post', size: '1080×1080' },
  { key: 'thumb', label: 'WhatsApp', size: 'Miniatura' },
] as const

function suggestedCopy(product: Product): string {
  const price = product.hasDiscount
    ? `¡Oferta! Ahora ${currency(product.price)} 🏷️`
    : `Disponible por ${currency(product.price)}`
  return `🔥 ${product.name}\n\n${price}\n\n👉 Comprá acá:`
}

export function SocialModal({
  product,
  onClose,
  banners,
}: {
  product: Product | null
  onClose: () => void
  banners?: BannersConfig
}) {
  const brand = useBrand()
  const [loading, setLoading] = useState(true)
  const [shortUrl, setShortUrl] = useState('')
  const [linkError, setLinkError] = useState(false)
  const [copy, setCopy] = useState('')
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    if (!product) return
    setCopy(suggestedCopy(product))
    setCopied(false)
    setLinkError(false)
    setLoading(true)
    setShortUrl('')

    let alive = true
    api
      .post<CreateShortlinkResponse>('/shortlink', {
        productId: product.productId,
        skuId: product.skuId,
        productName: product.name,
      })
      .then((res) => {
        if (!alive) return
        setShortUrl(buildVisibleShortUrl(res.code, brand.shortDomain, res.shortUrl))
      })
      .catch(() => {
        if (!alive) return
        // Fallback (§6.4): si falla el shortlink, se ofrece el link del producto.
        setShortUrl(product.productUrl)
        setLinkError(true)
      })
      .finally(() => alive && setLoading(false))

    return () => {
      alive = false
    }
  }, [product])

  const fullText = useMemo(() => `${copy}\n${shortUrl}`.trim(), [copy, shortUrl])

  if (!product) return null

  async function handleCopy() {
    try {
      await navigator.clipboard.writeText(fullText)
      setCopied(true)
      setTimeout(() => setCopied(false), 2500)
    } catch {
      setCopied(false)
    }
  }

  return (
    <div className="sheet-backdrop" onClick={onClose} role="dialog" aria-modal="true">
      <div className="sheet no-scrollbar overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="sheet-handle" />
        <button
          className="absolute right-3 top-3 rounded-full p-1.5 text-ink-muted hover:bg-surface-sunken"
          onClick={onClose}
          aria-label="Cerrar"
        >
          <X className="h-5 w-5" />
        </button>

        <div className="space-y-5 px-4 pb-8 pt-2">
          {/* Preview corporativo */}
          <div className="overflow-hidden rounded-2xl border border-line">
            <div className="flex items-center justify-between bg-surface-sunken px-3 py-2">
              <span className="text-xs font-semibold text-ink-soft">{brand.appName}</span>
              <span className="rounded bg-danger px-1.5 py-0.5 text-[10px] font-bold text-white">
                PROMO
              </span>
            </div>
            <div className="relative aspect-[4/3] bg-surface-sunken">
              {product.imageUrl && (
                <img src={product.imageUrl} alt={product.name} className="h-full w-full object-cover" />
              )}
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                <p className="line-clamp-1 text-sm font-semibold text-white">{product.name}</p>
                <p className="text-lg font-bold text-white">{currency(product.price)}</p>
              </div>
            </div>
          </div>

          {/* Copy sugerido (editable, no persiste — RN-08) */}
          <div>
            <label className="field-label">Copy sugerido</label>
            <textarea
              className="field-input min-h-[96px] resize-none"
              value={copy}
              onChange={(e) => setCopy(e.target.value)}
            />
          </div>

          {/* Link con UTMs */}
          <div>
            <label className="field-label">Tu link rastreable</label>
            <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-sunken px-3 py-2.5">
              <span className="flex-1 truncate text-sm text-ink-soft">
                {loading ? 'Generando link…' : shortUrl}
              </span>
            </div>
            {linkError && (
              <p className="mt-1 text-xs text-warning">
                No se pudo generar el shortlink; se usa el link del producto.
              </p>
            )}
          </div>

          {/* Banners (§6.4): imágenes estáticas subidas por el admin */}
          <div>
            <label className="field-label">Descargar banner</label>
            <div className="grid grid-cols-3 gap-2">
              {BANNER_FORMATS.map((f) => {
                const url = banners?.[f.key]
                return url ? (
                  <a
                    key={f.key}
                    href={url}
                    download
                    target="_blank"
                    rel="noreferrer"
                    className="btn-secondary flex-col gap-0.5 py-2.5 text-xs"
                  >
                    <Download className="h-4 w-4" />
                    <span className="font-semibold">{f.label}</span>
                    <span className="text-[10px] text-ink-faint">{f.size}</span>
                  </a>
                ) : (
                  <button
                    key={f.key}
                    className="btn-secondary flex-col gap-0.5 py-2.5 text-xs disabled:opacity-40"
                    disabled
                    title="El admin todavía no configuró este banner"
                  >
                    <Download className="h-4 w-4" />
                    <span className="font-semibold">{f.label}</span>
                    <span className="text-[10px] text-ink-faint">{f.size}</span>
                  </button>
                )
              })}
            </div>
          </div>

          {/* Toggle automatización (Fase 2, deshabilitado) */}
          <div className="flex items-center justify-between rounded-xl border border-line px-3 py-2.5 opacity-70">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-ink-muted" />
              <div>
                <p className="text-sm font-medium text-ink">Automatizar en mis estados</p>
                <p className="text-[11px] text-ink-faint">Próximamente (Fase 2)</p>
              </div>
            </div>
            <span className="h-5 w-9 rounded-full bg-line p-0.5">
              <span className="block h-4 w-4 rounded-full bg-white" />
            </span>
          </div>

          {/* CTA copiar */}
          <button className="btn-primary btn-block" onClick={handleCopy} disabled={loading}>
            {copied ? (
              <>
                <Check className="h-4 w-4" /> ¡Copiado!
              </>
            ) : (
              <>
                <Copy className="h-4 w-4" /> Copiar texto y link
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
